import { TableCell } from "@mui/material";
import {
	countMonthsBetweenDates,
	countWeeksBetweenDates,
	getWeekEndDate,
	isBetweenDates,
	getEndOfDayDate,
	isDateAfter,
	isDateInCurrentWeek,
	getWeekStartDate,
} from "../../../../../utils/dateUtils";
import { ServiceProvided, TreatmentPrescriptionSession } from "../../types";
import { activeServicesProvidedFilter, plannedServicesProvidedFilter } from "../../utils";
import { isSameDay } from "date-fns";
import { SingleValueCustomChip } from "../../../../../components/chips/SingleValueCustomChip";

type DoneVsExpectedTableCellProps = {
	currentServiceSessions: Array<TreatmentPrescriptionSession> | undefined;
	serviceProvided: ServiceProvided;
	staffUnavailableCount?: number;
};
const sessionDoneAndScheduleStatus = ["done", "booked", "confirmed", "awaiting_in_clinic", "in_session"];

export const DoneVsExpectedTableCell = ({ currentServiceSessions, serviceProvided, staffUnavailableCount = 0 }: DoneVsExpectedTableCellProps) => {
	const currentServSessionsDone = currentServiceSessions?.filter((session) => session.status === "done");
	const currrentServSessionsDoneOrSchedule =
		currentServiceSessions?.filter((session) => sessionDoneAndScheduleStatus.includes(session.status)) ?? [];

	if (!currentServSessionsDone || !serviceProvided.date_begin) return <TableCell></TableCell>;
	if (serviceProvided.session_periodicity === "not_applicable") {
		return (
			<TableCell align="center">
				<SingleValueCustomChip text="Não Aplicável" />
			</TableCell>
		);
	}
	const { expectedSessions, sessionsNumberToday } = makeExpectedSessions(serviceProvided, currrentServSessionsDoneOrSchedule, staffUnavailableCount);

	const displaySessionsDoneVSExpected = `${currentServSessionsDone.length} / ${expectedSessions}`;

	const isValidSessionsDoneVSExpected = currentServSessionsDone.length === expectedSessions;

	const willDoneBeEqualToExpectedToday = sessionsNumberToday > 0 && currentServSessionsDone.length + sessionsNumberToday === expectedSessions;
	let color = isValidSessionsDoneVSExpected ? "green" : "red";
	if (expectedSessions !== 0 && willDoneBeEqualToExpectedToday) color = "primary.main";

	return (
		<TableCell sx={{ color: color }} align="center">
			{displaySessionsDoneVSExpected}
		</TableCell>
	);
};

export const makeExpectedSessions = (
	serviceProvided: ServiceProvided,
	sessionsDoneOrSchedule: Array<TreatmentPrescriptionSession>,
	staffUnavailableCount: number = 0,
) => {
	const isPlannedServiceProvided = plannedServicesProvidedFilter(serviceProvided);
	if (isPlannedServiceProvided) return { expectedSessions: 0, sessionsNumberToday: 0 };
	const startDate = new Date(serviceProvided.date_begin);
	const endDate = serviceProvided.date_end ? new Date(serviceProvided.date_end) : new Date();

	const isStartDateAfterEndDate = isDateAfter(startDate, endDate);
	const isEndDateOnCurrentWeek = isDateInCurrentWeek(endDate) && !isStartDateAfterEndDate;

	const expectedSessions =
		countUnitsOnPeriodicity(serviceProvided, isEndDateOnCurrentWeek) *
		serviceProvided.session_quantity;

	let sessionsToAddFrom1stWeek = 0;
	if (serviceProvided.session_periodicity === "weekly") {
		sessionsToAddFrom1stWeek = countSessionsOnWeekByDates(sessionsDoneOrSchedule, {
			start: startDate,
			end: getWeekEndDate(startDate),
		});
	}

	let sessionsToAddFromCurrentWeek = 0;
	if (serviceProvided.session_periodicity === "weekly" && isEndDateOnCurrentWeek) {
		sessionsToAddFromCurrentWeek = countSessionsOnWeekByDates(sessionsDoneOrSchedule, {
			start: getWeekStartDate(endDate),
			end: getEndOfDayDate(endDate),
		});
	}

	const sessionsNumberToday = countSessionsToday(sessionsDoneOrSchedule);

	const totalExpected = expectedSessions + sessionsToAddFrom1stWeek + sessionsToAddFromCurrentWeek - staffUnavailableCount;

	return {
		expectedSessions: Math.max(0, totalExpected),
		sessionsNumberToday: sessionsNumberToday,
	};
};

const countUnitsOnPeriodicity = (
	servProv: ServiceProvided,
	isEndDateOnCurrentWeek: boolean
) => {
	const startDate = new Date(servProv.date_begin);
	const isTodayAfterStartDate = isDateAfter(new Date(), startDate);
	const isActiveService = activeServicesProvidedFilter(servProv);

	let actualEndDate: Date;
	
	if (isActiveService) {
		// Active services always use today as the end date
		actualEndDate = getEndOfDayDate(new Date());
	} else if (servProv.date_end) {
		// Use the specified end date if available
		actualEndDate = getEndOfDayDate(servProv.date_end);
	} else if (isTodayAfterStartDate) {
		// If no end date and service has started, use today
		actualEndDate = getEndOfDayDate(new Date());
	} else {
		// If service hasn't started yet, use the start date
		actualEndDate = getEndOfDayDate(startDate);
	}

	const is1stWeekCurrentWeek = isDateInCurrentWeek(startDate);

	if (servProv.session_periodicity === "weekly") {
		if (is1stWeekCurrentWeek) {
			// its no inclusive, already skips 1st week
			return countWeeksBetweenDates(startDate, actualEndDate);
		}
		if (isEndDateOnCurrentWeek) {
			// subtract 1 to remove the current week
			return countWeeksBetweenDates(startDate, actualEndDate) - 1;
		}

		return countWeeksBetweenDates(startDate, actualEndDate);
	}
	if (servProv.session_periodicity === "monthly") {
		// adds 1 to make it inclusive of the 1st month
		return countMonthsBetweenDates(startDate, actualEndDate) + 1;
	}
	return 0;
};

const countSessionsOnWeekByDates = (
	sessions: Array<TreatmentPrescriptionSession>,
	interval: {
		start: Date;
		end: Date;
	}
) => {
	let count = 0;
	sessions.forEach((session) => {
		if (isBetweenDates(new Date(session.start_date), interval.start, interval.end)) count++;
	});
	return count;
};

const countSessionsToday = (sessions: Array<TreatmentPrescriptionSession>) => {
	let count = 0;
	sessions.forEach((session) => {
		if (isSameDay(new Date(session.start_date), new Date())) count++;
	});
	return count;
};
